import type { Language } from '../i18n/translations'

export type FAQCategoryId =
  | 'general'
  | 'reports'
  | 'payment'
  | 'consultations'
  | 'astrologers'
  | 'tech'

export interface FAQCategory {
  id: FAQCategoryId
  icon: string // lucide icon name
}

export interface FAQItem {
  id: string
  categoryId: FAQCategoryId
  question: string
  answer: string
}

export const FAQ_CATEGORIES: FAQCategory[] = [
  { id: 'general', icon: 'HelpCircle' },
  { id: 'reports', icon: 'FileText' },
  { id: 'payment', icon: 'CreditCard' },
  { id: 'consultations', icon: 'Users' },
  { id: 'astrologers', icon: 'Star' },
  { id: 'tech', icon: 'Settings' },
]

export const FAQ_CATEGORY_LABELS: Record<
  Language,
  Record<FAQCategoryId, string>
> = {
  ru: {
    general: 'Общие вопросы',
    reports: 'Отчёты и услуги',
    payment: 'Оплата и возврат',
    consultations: 'Консультации',
    astrologers: 'Для астрологов',
    tech: 'Технические вопросы',
  },
  en: {
    general: 'General',
    reports: 'Reports & Services',
    payment: 'Payment & Refunds',
    consultations: 'Consultations',
    astrologers: 'For Astrologers',
    tech: 'Technical',
  },
  zh: {
    general: '常见问题',
    reports: '报告与服务',
    payment: '支付与退款',
    consultations: '咨询',
    astrologers: '占星师专区',
    tech: '技术问题',
  },
  es: {
    general: 'General',
    reports: 'Informes y Servicios',
    payment: 'Pagos y Reembolsos',
    consultations: 'Consultas',
    astrologers: 'Para Astrólogos',
    tech: 'Técnico',
  },
}

export const FAQ_DATA: FAQItem[] = [
  // === Общие вопросы ===
  {
    id: 'g1',
    categoryId: 'general',
    question: 'Что такое AstroGuide?',
    answer:
      'AstroGuide — это современная астрологическая платформа, которая объединяет профессиональные алгоритмы расчета натальных карт, подробные автоматические отчеты и маркетплейс проверенных астрологов для личных консультаций.',
  },
  {
    id: 'g2',
    categoryId: 'general',
    question: 'Как рассчитать свою натальную карту бесплатно?',
    answer:
      'Перейдите в раздел «Бесплатная карта», введите дату, точное время и место вашего рождения. Система мгновенно построит вашу карту и предоставит краткую интерпретацию основных положений — Солнце, Луна, Асцендент.',
  },
  {
    id: 'g3',
    categoryId: 'general',
    question: 'Насколько точны ваши расчеты?',
    answer:
      'Наши алгоритмы используют швейцарские эфемериды (Swiss Ephemeris), которые обеспечивают высочайшую астрономическую точность расчета положения планет, домов и аспектов, сопоставимую с профессиональным десктопным ПО.',
  },
  {
    id: 'g4',
    categoryId: 'general',
    question: 'Нужно ли регистрироваться для использования платформы?',
    answer:
      'Бесплатный расчёт карты доступен без регистрации. Для заказа платных отчётов, консультаций и сохранения истории расчётов потребуется создать аккаунт — это занимает менее минуты.',
  },
  {
    id: 'g5',
    categoryId: 'general',
    question: 'Чем AstroGuide отличается от других астрологических сайтов?',
    answer:
      'Мы сочетаем профессиональные алгоритмы расчёта (швейцарские эфемериды), детализированные автоматические отчёты на 30–40 страниц и маркетплейс проверенных астрологов. Все отчёты генерируются мгновенно, а астрологи проходят строгую модерацию.',
  },

  // === Отчёты и услуги ===
  {
    id: 'r1',
    categoryId: 'reports',
    question: 'В чем разница между Beta и Premium отчетом?',
    answer:
      'Beta-отчет — это краткая выжимка на 2–3 страницы по основным показателям карты (Солнце, Луна, Асцендент). Premium-отчет — глубокий анализ на 30–40 страниц, который охватывает все сферы: личность, таланты, карьеру, финансы, отношения и кармические задачи.',
  },
  {
    id: 'r2',
    categoryId: 'reports',
    question: 'Как быстро я получу заказанный Premium отчет?',
    answer:
      'Premium отчет генерируется автоматически сразу после оплаты — обычно за 10–30 секунд. Он будет доступен в Личном кабинете и отправлен в формате PDF на ваш email.',
  },
  {
    id: 'r3',
    categoryId: 'reports',
    question: 'Что такое Соляр и когда его лучше заказывать?',
    answer:
      'Соляр — это прогноз на персональный год, который строится на момент возвращения Солнца в натальное положение (ваш день рождения). Лучше заказывать за 1–2 месяца до дня рождения, чтобы подготовиться к новым энергиям года.',
  },
  {
    id: 'r4',
    categoryId: 'reports',
    question: 'Что входит в отчёт по совместимости (синастрию)?',
    answer:
      'Отчёт по совместимости анализирует взаимодействие двух натальных карт: эмоциональную связь (Луна), притяжение (Венера-Марс), коммуникацию (Меркурий), кармические связи (лунные узлы) и зоны роста (Сатурн). Вы получите подробный PDF с рекомендациями.',
  },
  {
    id: 'r5',
    categoryId: 'reports',
    question: 'Можно ли заказать отчёт в подарок?',
    answer:
      'Да! При оформлении заказа укажите данные рождения получателя. Отчёт будет отправлен на ваш email, и вы сможете переслать его в удобном формате. Мы также можем оформить подарочный сертификат.',
  },
  {
    id: 'r6',
    categoryId: 'reports',
    question: 'Что такое Комбо-отчёт?',
    answer:
      'Комбо — это два отчёта в одном: полный анализ натальной карты + прогноз на год (соляр). Вы получаете и глубокое понимание себя, и практический прогноз. Комбо выгоднее на 20% по сравнению с покупкой отчётов по отдельности.',
  },

  // === Оплата и возврат ===
  {
    id: 'p1',
    categoryId: 'payment',
    question: 'Какие способы оплаты вы принимаете?',
    answer:
      'Мы принимаем банковские карты (Visa, MasterCard, МИР), СБП, Yandex Pay и Tinkoff Pay. Все платежи проходят через защищённый шлюз с шифрованием данных.',
  },
  {
    id: 'p2',
    categoryId: 'payment',
    question: 'Можно ли вернуть деньги, если отчет не понравился?',
    answer:
      'Автоматические отчеты — цифровой продукт мгновенной доставки, возврат по умолчанию не предусмотрен. Однако при технической ошибке или неточности в расчётах обратитесь в поддержку — мы обязательно решим проблему.',
  },
  {
    id: 'p3',
    categoryId: 'payment',
    question: 'Безопасна ли оплата на сайте?',
    answer:
      'Абсолютно. Мы не храним данные банковских карт — все платежи обрабатываются через сертифицированный платёжный шлюз с протоколом 3D Secure и шифрованием SSL/TLS.',
  },
  {
    id: 'p4',
    categoryId: 'payment',
    question: 'Есть ли рассрочка или подписка?',
    answer:
      'Сейчас мы работаем над подпиской, которая даст доступ ко всем отчётам и скидки на консультации. Следите за обновлениями! Рассрочка доступна для заказов от 5 000 ₽ через Tinkoff.',
  },

  // === Консультации ===
  {
    id: 'c1',
    categoryId: 'consultations',
    question: 'Как проходит онлайн-консультация с астрологом?',
    answer:
      'После выбора астролога и оплаты вы согласуете удобное время. Консультация проходит по видеосвязи (Zoom, Telegram, Google Meet). Астролог заранее изучает вашу карту. Во время сессии можно задавать любые вопросы. По итогу вы получаете запись встречи.',
  },
  {
    id: 'c2',
    categoryId: 'consultations',
    question: 'Как вы проверяете астрологов на платформе?',
    answer:
      'Каждый астролог проходит строгую модерацию: проверка дипломов и сертификатов, тестовое собеседование, анализ отзывов клиентов. Значки «PRO» и «TOP» выдаются только специалистам с подтверждённым опытом и высоким рейтингом.',
  },
  {
    id: 'c3',
    categoryId: 'consultations',
    question: 'Сколько стоит консультация?',
    answer:
      'Стоимость зависит от астролога: от 3 000 ₽ за 60 минут у начинающих специалистов до 15 000 ₽ у топовых экспертов. Точные цены указаны в профиле каждого астролога.',
  },
  {
    id: 'c4',
    categoryId: 'consultations',
    question: 'Можно ли отменить или перенести консультацию?',
    answer:
      'Да, бесплатная отмена или перенос возможны не позднее чем за 24 часа до назначенного времени. При отмене менее чем за 24 часа удерживается 50% стоимости.',
  },

  // === Для астрологов ===
  {
    id: 'a1',
    categoryId: 'astrologers',
    question: 'Как стать экспертом на вашей платформе?',
    answer:
      'Перейдите в раздел «Астрологам» и заполните заявку. Потребуется информация об образовании, опыте работы и ссылки на соцсети или сайт. Наша команда свяжется с вами в течение 3 рабочих дней.',
  },
  {
    id: 'a2',
    categoryId: 'astrologers',
    question: 'Какую комиссию берет платформа?',
    answer:
      'Базовая комиссия — 20% от стоимости консультации. В неё входят эквайринг, маркетинг, привлечение клиентов и техподдержка вашего профиля. Для топовых астрологов предусмотрены индивидуальные условия.',
  },
  {
    id: 'a3',
    categoryId: 'astrologers',
    question: 'Как привлекать больше клиентов на платформе?',
    answer:
      'Заполните профиль максимально подробно: фото, описание подхода, специализации, отзывы. Публикуйте статьи в блоге AstroGuide. Астрологи с заполненным профилем и высоким рейтингом получают приоритет в каталоге.',
  },
  {
    id: 'a4',
    categoryId: 'astrologers',
    question: 'Могу ли я размещать рекламу на платформе?',
    answer:
      'Да, мы предлагаем рекламные возможности для астрологов и смежных бизнесов. Подробности — на странице «Реклама» или по запросу через форму обратной связи.',
  },

  // === Технические вопросы ===
  {
    id: 't1',
    categoryId: 'tech',
    question: 'Безопасны ли мои личные данные?',
    answer:
      'Да, мы серьёзно относимся к конфиденциальности. Данные о рождении используются исключительно для расчётов и не передаются третьим лицам. Вы можете удалить аккаунт и все данные в любой момент через настройки.',
  },
  {
    id: 't2',
    categoryId: 'tech',
    question: 'Я забыл точное время рождения. Что делать?',
    answer:
      'Точное время критически важно для построения домов (Асцендент и куспиды). Если не знаете — закажите услугу «Ректификация» у наших астрологов. Это процедура восстановления времени рождения по ключевым событиям жизни.',
  },
  {
    id: 't3',
    categoryId: 'tech',
    question: 'Отчёт не пришёл на email. Что делать?',
    answer:
      'Проверьте папку «Спам» — иногда письма попадают туда. Отчёт также всегда доступен в вашем Личном кабинете. Если проблема сохраняется — напишите в поддержку, мы отправим повторно.',
  },
  {
    id: 't4',
    categoryId: 'tech',
    question: 'Поддерживаете ли вы мобильные устройства?',
    answer:
      'Да, платформа полностью адаптивна и работает на любых устройствах: смартфонах, планшетах и компьютерах. Мобильное приложение находится в разработке.',
  },
]

// UI translations for FAQ page
export const FAQ_UI: Record<Language, Record<string, string>> = {
  ru: {
    title: 'Частые вопросы',
    subtitle:
      'Найдите ответы на вопросы об отчётах, консультациях и работе платформы AstroGuide',
    searchPlaceholder: 'Найти ответ (например: возврат, соляр, оплата)...',
    categories: 'Категории',
    searchResults: 'Результаты поиска',
    found: 'Найдено',
    notFound: 'Ничего не найдено',
    notFoundHint:
      'По вашему запросу нет ответов. Попробуйте изменить формулировку.',
    resetSearch: 'Сбросить поиск',
    helpful: 'Полезно?',
    yes: 'Да',
    no: 'Нет',
    thankYou: 'Спасибо за отзыв!',
    ctaTitle: 'Не нашли ответ на свой вопрос?',
    ctaSubtitle:
      'Наша служба поддержки всегда готова помочь вам разобраться с любыми трудностями.',
    ctaEmail: 'Написать в поддержку',
    ctaAstrologer: 'Найти астролога',
    breadcrumbHome: 'Главная',
    breadcrumbFaq: 'Вопросы и ответы',
    questionsCount: 'вопросов',
  },
  en: {
    title: 'FAQ',
    subtitle:
      'Find answers about reports, consultations and the AstroGuide platform',
    searchPlaceholder: 'Find an answer (e.g. refund, solar, payment)...',
    categories: 'Categories',
    searchResults: 'Search Results',
    found: 'Found',
    notFound: 'Nothing found',
    notFoundHint: 'No answers for your query. Try rephrasing.',
    resetSearch: 'Reset search',
    helpful: 'Helpful?',
    yes: 'Yes',
    no: 'No',
    thankYou: 'Thanks for your feedback!',
    ctaTitle: "Didn't find your answer?",
    ctaSubtitle: 'Our support team is always ready to help.',
    ctaEmail: 'Contact Support',
    ctaAstrologer: 'Find an Astrologer',
    breadcrumbHome: 'Home',
    breadcrumbFaq: 'FAQ',
    questionsCount: 'questions',
  },
  zh: {
    title: '常见问题',
    subtitle: '查找有关报告、咨询和AstroGuide平台的答案',
    searchPlaceholder: '查找答案（例如：退款、太阳回归、支付）...',
    categories: '分类',
    searchResults: '搜索结果',
    found: '找到',
    notFound: '未找到',
    notFoundHint: '没有找到相关答案。请尝试修改搜索词。',
    resetSearch: '重置搜索',
    helpful: '有帮助吗？',
    yes: '是',
    no: '否',
    thankYou: '感谢您的反馈！',
    ctaTitle: '没有找到答案？',
    ctaSubtitle: '我们的支持团队随时准备帮助您。',
    ctaEmail: '联系支持',
    ctaAstrologer: '找占星师',
    breadcrumbHome: '首页',
    breadcrumbFaq: '常见问题',
    questionsCount: '个问题',
  },
  es: {
    title: 'Preguntas Frecuentes',
    subtitle:
      'Encuentra respuestas sobre informes, consultas y la plataforma AstroGuide',
    searchPlaceholder: 'Buscar respuesta (ej: reembolso, solar, pago)...',
    categories: 'Categorías',
    searchResults: 'Resultados de búsqueda',
    found: 'Encontrados',
    notFound: 'Nada encontrado',
    notFoundHint: 'No hay respuestas para tu consulta. Intenta reformular.',
    resetSearch: 'Restablecer búsqueda',
    helpful: '¿Útil?',
    yes: 'Sí',
    no: 'No',
    thankYou: '¡Gracias por tu opinión!',
    ctaTitle: '¿No encontraste tu respuesta?',
    ctaSubtitle: 'Nuestro equipo de soporte está siempre listo para ayudarte.',
    ctaEmail: 'Contactar Soporte',
    ctaAstrologer: 'Buscar Astrólogo',
    breadcrumbHome: 'Inicio',
    breadcrumbFaq: 'FAQ',
    questionsCount: 'preguntas',
  },
}

// Helper: count questions per category
export function getQuestionCount(categoryId: FAQCategoryId): number {
  return FAQ_DATA.filter((q) => q.categoryId === categoryId).length
}
